import React, { useContext, useEffect, useState } from "react";
import { Actions, ContentContainer, Grid, SummaryLabels, WhiteContainer } from "../../../../../styles/orderform.tsx";
import { OrderContext } from "../../OrderContext.tsx";
import AddressForm from "../Common/AddressForm.tsx";
import ClosedWhiteContainer from "../Common/ClosedWhiteContainer.tsx";
import { addressRegex, b2bInvoiceTypes, contractTypes, emailRegex, phoneRegex } from "../../data.ts";
import { AddressStatus, Address as AddressType, ValidationErrors } from "../../types.ts";
import FormTextInput from "./FormTextInput.tsx";
import dayjs from "dayjs";
import { isKnownEinvoiceOperator, isValidBusinessId, isValidEinvoiceAddress } from "../../../../../misc/validations.ts";
import { getAddress } from "../../../../../misc/helpers.ts";
import Address from "../Common/Address.tsx";
import DateInput from "../Common/DateInput.tsx";
import ErrorWrapper from "../Common/ErrorWrapper.tsx";
import { isValid as isValidIBAN } from "iban";
import HorizontalLine from "./HorizontalLine.tsx";
import ContractTypeText from "../Common/ContractTypeText.tsx";
import Dialog from "../Common/Dialog.tsx";
import { styled } from "styled-components";
import { useTranslation } from "react-i18next";
import Info from '../../../../../assets/images/icon-info.svg?react';

const B2bPhaseFour = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .basic-title {
    font-size: 22px;
    font-family: covik-sans-mono, sans-serif;
    font-weight: 700;
    margin-top: 16px;
    margin-bottom: 8px;
  }

  .sub-title {
    font-size: 18px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .other-title {
    font-size: 18px;
    font-weight: 700;
    margin: 10px 0;
  }
`;

function PhaseFour() {
  const { formValues, setFormValues, handleFormChange, toPhase } = useContext(OrderContext);
  const { t } = useTranslation();

  const [minDate, setMinDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [maxDate, setMaxDate] = useState(dayjs().format('YYYY-MM-DD'));
  const firstMeteringPoint = formValues['metering-points'][0];
  const [sameAsPostal, setSameAsPostal] = useState<true | false | undefined>(
    firstMeteringPoint && firstMeteringPoint.address && formValues['location-street'] === firstMeteringPoint.address.streetName
    && formValues["location-house"] === firstMeteringPoint.address.houseNumber
    && formValues["location-postalcode"] === firstMeteringPoint.address.zipCode
    && formValues["location-city"] === firstMeteringPoint.address.cityName
    && formValues["location-stairway"] === firstMeteringPoint.address.houseLetter
    && formValues["location-flat"] === firstMeteringPoint.address.residence
  );
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});
  const [stepErrors, setStepErrors] = useState<ValidationErrors>({});
  const [errorStep, setErrorStep] = useState<1 | 2 | 3 | undefined>(undefined);
  const [showErrorStep, setShowErrorStep] = useState<1 | 2 | 3 | undefined>(undefined);
  const [dialogVisible, setDialogVisible] = useState(false);

  const validateFirstStep = (all = false) => {
    // validate fields
    const validationErrors: ValidationErrors = {};
    // Required fields
    if (all) {
      const requiredFields = [
        "company-name", "organization-no", "contact-firstname", "contact-lastname", "contact-email", "contact-phone",
        "location-postalcode", "location-street", "location-city", "location-house"
      ]
      for (let i = 0; i < requiredFields.length; i++) {
        const fieldName = requiredFields[i];
        const value = formValues[fieldName as keyof typeof formValues]
        if (!value) {
          validationErrors[fieldName] = t('errors.required')
        }
      }
    }
    // Validate fields
    if (formValues['location-house']) {
      if (!addressRegex.test(formValues['location-house'])) {
        validationErrors['location-house'] = t('errors.plsCheck');
      }
    }
    if (formValues['invoice-house']) {
      const houseRegex = /^[a-zA-Z0-9.\-\/]+$/;
      if (!houseRegex.test(formValues['invoice-house'])) {
        validationErrors['invoice-house'] = t('errors.plsCheck');
      }
    }
    const postalCodeRegex = /^[0-9]{5}$/;
    if (formValues['location-postalcode']) {
      if (!postalCodeRegex.test(formValues['location-postalcode'])) {
        validationErrors['location-postalcode'] = t('errors.invalidPostalcode');
      }
    }
    if (formValues['invoice-postalcode']) {
      if (!postalCodeRegex.test(formValues['invoice-postalcode'])) {
        validationErrors['invoice-postalcode'] = t('errors.invalidPostalcode');
      }
    }
    if (formValues['location-stairway'] && !addressRegex.test(formValues['location-stairway'])) {
      validationErrors['location-stairway'] = t('errors.plsCheck');
    }
    if (formValues['invoice-stairway'] && !addressRegex.test(formValues['invoice-stairway'])) {
      validationErrors['invoice-stairway'] = t('errors.plsCheck');
    }
    if (formValues['location-flat'] && !addressRegex.test(formValues['location-flat'])) {
      validationErrors['location-flat'] = t('errors.plsCheck');
    }
    if (formValues['invoice-flat'] && !addressRegex.test(formValues['invoice-flat'])) {
      validationErrors['invoice-flat'] = t('errors.plsCheck');
    }
    if (formValues['contact-email'] && !emailRegex.test(formValues['contact-email'])) {
      validationErrors['contact-email'] = t('errors.invalidEmail');
    }
    if (formValues['contact-phone'] && !phoneRegex.test(formValues['contact-phone'])) {
      validationErrors['contact-phone'] = t('errors.invalidPhone');
    }
    if (formValues['organization-no'] && isValidBusinessId(formValues['organization-no']) === false) {
      validationErrors['organization-no'] = t('errors.invalidBusinessId');
    }
    return validationErrors;
  }

  const validateSecondStep = (all = false) => {
    const validationErrors: ValidationErrors = {};

    if (all) {
      if (!formValues['contract-type']) {
        validationErrors['contract-type'] = t('errors.required');
      }

      if (sameAsPostal === undefined) {
        validationErrors['same-as'] = t('errors.required');
      }
    }


    // Validate metering points also
    const meteringPoints = formValues['metering-points'];
    const requiredAddressFields = ['zipCode', 'cityName', 'streetName', 'houseNumber'];
    for (let i = 0; i < meteringPoints.length; i++) {
      const point = meteringPoints[i];

      if (all) {
        if (!point.startDate) {
          validationErrors[`metering-point-${i}-startDate`] = t('errors.required');
        }
        if (!(point.status === 'confirmed' || point.status === 'skipped')) {
          validationErrors[`metering-point-${i}-status`] = t('errors.required');
        }
        if (!point.address) {
          validationErrors[`metering-point-${i}`] = t('errors.required');
        }
      }

      if (point.address) {
        for (let j = 0; j < requiredAddressFields.length; j++) {
          const fieldName = requiredAddressFields[j];
          const value = point.address[fieldName as keyof AddressType];

          if (!value) {
            if (all) {
              validationErrors[`metering-point-${i}-${fieldName}`] = t('errors.required');
            }
          } else {
            if (fieldName === 'zipCode' && !/^\d{5}$/.test(value)) {
              validationErrors[`metering-point-${i}-${fieldName}`] = t('errors.invalidPostalcode');
            }
            if (fieldName === 'houseNumber' && !addressRegex.test(value)) {
              validationErrors[`metering-point-${i}-${fieldName}`] = t('errors.plsCheck');
            }
            if (fieldName === 'houseLetter' && value && !addressRegex.test(value)) {
              validationErrors[`metering-point-${i}-${fieldName}`] = t('errors.plsCheck');
            }
            if (fieldName === 'residence' && value && !addressRegex.test(value)) {
              validationErrors[`metering-point-${i}-${fieldName}`] = t('errors.plsCheck');
            }
          }
        }
      }
      if (point.startDate) {
        const dateString = point.startDate;
        const [day, month, year] = dateString.split('.');
        const parsedDate = dayjs(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
        // Between min and max date
        if (parsedDate.isBefore(minDate) || parsedDate.isAfter(maxDate)) {
          const minD = dayjs(minDate).format('DD.MM.YYYY');
          const maxD = dayjs(maxDate).format('DD.MM.YYYY');
          validationErrors[`metering-point-${i}-startDate`] = t('errors.dateRange', {minD, maxD});
        }
      }
    }

    return validationErrors;
  }

  const validateThirdStep = (all = false) => {
    const validationErrors: ValidationErrors = {};
    if (all &&!formValues['invoice-method']) {
      validationErrors['invoice-method'] = t('errors.required');
    }
    
    if (formValues['invoice-method'] === 'b2b-einvoice') {
      if (all) {
        if (!formValues['b2b-operator']) {
          validationErrors['b2b-operator'] = t('errors.required');
        }
        if (!formValues['b2b-address']) {
          validationErrors['b2b-address'] = t('errors.required');
        }
      }
      if (formValues['b2b-operator']) {
        const validOperator = isKnownEinvoiceOperator(formValues['b2b-operator']);
        if (!validOperator) {
          validationErrors['b2b-operator'] = t('errors.invalidOperatorId');
        }
      }
      if (formValues['b2b-address']) {
        const validIBAN = isValidIBAN(formValues['b2b-address']);
        const validOVT = isValidEinvoiceAddress(formValues['b2b-address']);
        if (!validIBAN && !validOVT) {
          validationErrors['b2b-address'] = t('errors.invalidIBAN');
        }
      }
    } else if (formValues['invoice-method'] === 'email') {
      if (all) {
        if (!formValues['b2b-email']) {
          validationErrors['b2b-email'] = t('errors.required');
        }
      }
      if (formValues['b2b-email'] && !emailRegex.test(formValues['b2b-email'])) { 
        validationErrors['b2b-email'] = t('errors.invalidEmail');
      }
    }
    
    if (formValues['separated-invoicing-address'] === 'on') {
      const requiredFields = ['invoice-street', 'invoice-house', 'invoice-postalcode', 'invoice-city'];
      for (let i = 0; i < requiredFields.length; i++) {
        const fieldName = requiredFields[i];
        const value = formValues[fieldName as keyof typeof formValues]
        if (all && !value) {
          validationErrors[fieldName] = t('errors.required')
        } 
        if (value) {
          if (fieldName === 'invoice-postalcode' && !/^\d{5}$/.test(value as string)) {
            validationErrors[fieldName] = t('errors.invalidPostalcode');
          }
          if (fieldName === 'invoice-house' && !addressRegex.test(value as string)) {
            validationErrors[fieldName] = t('errors.plsCheck');
          }
          if (fieldName === 'invoice-stairway' && value && !addressRegex.test(value as string)) {
            validationErrors[fieldName] = t('errors.plsCheck');
          }
          if (fieldName === 'invoice-flat' && value && !addressRegex.test(value as string)) {
            validationErrors[fieldName] = t('errors.plsCheck');
          }
        }
      }
    }
    if (formValues['invoice-reference'] && formValues['invoice-reference'].length > 50) {
      validationErrors['invoice-reference'] = t('errors.maxLengthRefenrece');
    }
    return validationErrors;
  }

  useEffect(() => {
    let meteringPoint = undefined;
    const meteringPoints = formValues['metering-points'];
    if (meteringPoints.length > 0) {
      meteringPoint = meteringPoints[0];
    } else {
      const address: AddressType = { streetName: '', houseNumber: '', houseLetter: '', residence: '', zipCode: '', cityName: '' };
      meteringPoint = { startDate: '', address };
    }

    if (sameAsPostal === true) {
      meteringPoint.address = {
        streetName: formValues['location-street'],
        houseNumber: formValues['location-house'],
        houseLetter: formValues['location-stairway'],
        residence: formValues['location-flat'],
        zipCode: formValues['location-postalcode'],
        cityName: formValues['location-city'],
      };
    } else {
      meteringPoint.address = {
        streetName: '',
        houseNumber: '',
        houseLetter: '',
        residence: '',
        zipCode: '',
        cityName: '',
      };
    }

    const newMeteringPoints = [...meteringPoints]
    newMeteringPoints[0] = meteringPoint;
    setFormValues({ ...formValues, 'metering-points': newMeteringPoints });

  }, [sameAsPostal]);

  const toStep = (step: 1 | 2 | 3) => {
    setErrorStep(currentStep);

    let nextStep: 0 | 1 | 2 | 3 = 0
    if (step === 1) {
      nextStep = step;
    } else if (step === 2) {
      // validate step 1 fields before moving to step 2
      const errors = validateFirstStep(true);
      const isValid = Object.keys(errors).length === 0;
      if (isValid) {
        nextStep = step;
        setFormErrors({});
      } else {
        setShowErrorStep(1);
        setFormErrors(errors);
      }
    } else if (step === 3) {
      // validate step 2 fields before moving to step 3
      const errors = { ...validateSecondStep(true), ...validateFirstStep(true) };
      const isValid = Object.keys(errors).length === 0;
      if (isValid) {
        nextStep = step;
        setFormErrors({});
      } else {
        setShowErrorStep(2);
        setFormErrors(errors);
      }
    }
    if (nextStep === 0) {
      return;
    } else {
      setCurrentStep(nextStep);
      scrollTo(`step${nextStep}`);
    }
  }

  const updateMeteringPoint = ({ index, address, startDate, diffStart, status }: { index: number, address?: Partial<AddressType>, startDate?: string, diffStart?: boolean, status?: AddressStatus }) => {
    const updatedMeteringPoints = [...formValues['metering-points']];
    if (address) {
      updatedMeteringPoints[index].address = {
        ...updatedMeteringPoints[index].address,
        zipCode: address.zipCode ?? updatedMeteringPoints[index].address.zipCode,
        cityName: address.cityName ?? updatedMeteringPoints[index].address.cityName,
        streetName: address.streetName ?? updatedMeteringPoints[index].address.streetName,
        houseNumber: address.houseNumber ?? updatedMeteringPoints[index].address.houseNumber,
        houseLetter: address.houseLetter ?? updatedMeteringPoints[index].address.houseLetter,
        residence: address.residence ?? updatedMeteringPoints[index].address.residence,
        careOf: address.careOf ?? updatedMeteringPoints[index].address.careOf,
      };
    }
    if (startDate) {
      updatedMeteringPoints[index].startDate = startDate;
      if (index === 0) {
        // wait for the state to update
        setTimeout(() => {
          handleFormChange({ target: { name: 'contract-startdate', value: startDate } } as React.ChangeEvent<HTMLInputElement>);
        }, 200)
      }
    } else if (startDate === '') {
      updatedMeteringPoints[index].startDate = '';
    }
    if (diffStart !== undefined) {
      updatedMeteringPoints[index].diffStart = diffStart;
    }
    if (status) {
      updatedMeteringPoints[index].status = status;
    }
    setFormValues({ ...formValues, 'metering-points': updatedMeteringPoints });
  }

  const addMeteringPoint = () => {
    const meteringPoints = formValues['metering-points'];
    const firstPoint = meteringPoints[0];
    const address: AddressType = { streetName: '', houseNumber: '', houseLetter: '', residence: '', zipCode: '', cityName: '' };
    const meteringPoint = { startDate: firstPoint.startDate, address, diffStart: !firstPoint.startDate ? true : false };
    setFormValues({ ...formValues, 'metering-points': [...formValues['metering-points'], meteringPoint] });
  }

  const deleteMeteringPoint = (index: number) => {
    const updatedMeteringPoints = formValues['metering-points'].filter((point, i) => i !== index);
    setFormValues({ ...formValues, 'metering-points': updatedMeteringPoints });
  }

  const toNextPhase = () => {
    setErrorStep(3);
    const firstStepErrors = validateFirstStep(true);
    const secondStepErrors = validateSecondStep(true);
    const thirdStepErrors = validateThirdStep(true);
    if (Object.keys(firstStepErrors).length === 0 && Object.keys(secondStepErrors).length === 0 && Object.keys(thirdStepErrors).length === 0) {
      toPhase(5);
    } else {
      setFormErrors({ ...firstStepErrors, ...secondStepErrors, ...thirdStepErrors });
      setShowErrorStep(3);
    }
  }

  const scrollTo = (elementId: string) => {
    // wait for the state to update
    if (!elementId) {
      return;
    }
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  useEffect(() => {
    const separate = formValues['separated-invoicing-address'] === 'on';
    const data = {
      'invoice-street': separate ? '' : formValues['location-street'],
      'invoice-house': separate ? '' : formValues['location-house'],
      'invoice-stairway': separate ? '' : formValues['location-stairway'],
      'invoice-flat': separate ? '' : formValues['location-flat'],
      'invoice-postalcode': separate ? '' : formValues['location-postalcode'],
      'invoice-city': separate ? '' : formValues['location-city'],
    }
    setFormValues({ ...formValues, ...data });
  }, [formValues['separated-invoicing-address']])

  const handleSeparateInvoicingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const data = { name: 'separated-invoicing-address', value: value, checked: value === 'on' };
    handleFormChange({ target: data } as React.ChangeEvent<HTMLInputElement>);
  }

  useEffect(() => {
    const currentValidator = errorStep === 1 ? validateFirstStep : errorStep === 2 ? validateSecondStep : errorStep === 3 ? validateThirdStep : validateFirstStep;

    const handleBlur = (event: FocusEvent) => {
      let errors = currentValidator(errorStep && errorStep === showErrorStep);
      setFormErrors(errors);
    }
    document.addEventListener('focusout', handleBlur);
    return () => {
      document.removeEventListener('focusout', handleBlur);
    };
  }, [formValues, errorStep]);

  useEffect(() => {
    const currentValidator = currentStep === 1 ? validateFirstStep : currentStep === 2 ? validateSecondStep : currentStep === 3 ? validateThirdStep : validateFirstStep;
    const currentStepErrors = currentValidator(true);
    setStepErrors(currentStepErrors);
    if (currentStep === errorStep) {
      setFormErrors(currentStepErrors);
    }
  }, [currentStep, errorStep,, showErrorStep, formValues]);

  return (
    <B2bPhaseFour>
      {currentStep !== 1 && <ClosedWhiteContainer text={t('phaseFour.customerInfo')} value={currentStep > 1 ? t('common.edit') : ''} handleClick={() => toStep(1)} />}
      {currentStep === 1 && <WhiteContainer id="step1">
        <h2>{t('phaseFour.customerInfo')}</h2>
        <p dangerouslySetInnerHTML={{ __html: t('phaseFour.unionNote') }}></p>
        <Grid>
          <FormTextInput label={t("phaseFour.companyName")} fieldName="company-name" value={formValues['company-name']} error={formErrors['company-name']} required />
          <FormTextInput label={t("phaseFour.companyId")} fieldName="organization-no" value={formValues['organization-no']} error={formErrors['organization-no']} required />
        </Grid>
        <div className="basic-title">{t('phaseFour.companyAddress')}</div>
        <AddressForm type="location" validationErrors={formErrors} disableAddressCheck />
        <div className="basic-title">{t('phaseFour.contactPerson')}</div>
        <Grid>
          <FormTextInput label={t("phaseFour.contactFirstName")} fieldName="contact-firstname" value={formValues['contact-firstname']} error={formErrors['contact-firstname']} required />
          <FormTextInput label={t("phaseFour.contactLastName")} fieldName="contact-lastname" value={formValues['contact-lastname']} error={formErrors['contact-lastname']} required />
          <FormTextInput label={t("phaseFour.contactEmail")} type="email" fieldName="contact-email" value={formValues['contact-email']} error={formErrors['contact-email']} required />
          <FormTextInput label={t("phaseFour.contactPhone")} type="tel" fieldName="contact-phone" value={formValues['contact-phone']} error={formErrors['contact-phone']} required />
        </Grid>
      </WhiteContainer>}

      {currentStep === 1 && <Actions>
        <button type="button" className={"primary" + (Object.keys(stepErrors).length > 0 ? ' disabled' : '')} onClick={() => toStep(2)}>{t('common.next')}</button>
      </Actions>}

      {currentStep !== 2 && <ErrorWrapper showError={false}>
        <ClosedWhiteContainer text={t("phaseFour.placeOfUse")} value={currentStep > 2 ? t('common.edit') : ''} handleClick={() => toStep(2)} />
      </ErrorWrapper>}
      {currentStep === 2 && <WhiteContainer id="step2">
        <h2>{t('phaseFour.placeOfUse')}</h2>
        <div className="sub-title">{t('phaseFour.contract')} <Info height={24} color="var(--Lumme)" onClick={() => setDialogVisible(true)} /></div>
        <Dialog isVisible={dialogVisible} onClose={() => setDialogVisible(false)}>
          <ContractTypeText all={true} updateMaxDate={() => { }} updateMinDate={() => { }} />
        </Dialog>
        <ErrorWrapper showError={!!formErrors['contract-type']}>
          <SummaryLabels className={!!formErrors['contract-type'] ? 'invalid' : ''}>
            {contractTypes.map((option) => (

              <label
                htmlFor={option.value}
                className="border radio-label"
                key={option.value}>
                <div className="header">
                  <input
                    type="radio"
                    id={option.value}
                    name="contract-type"
                    value={option.value}
                    checked={option.value === formValues['contract-type']}
                    onChange={handleFormChange}
                  />
                  <div className="title">{t(option.label)}</div>
                </div>
                <div className="description">{t(option.description)}</div>
              </label>
            ))}
          </SummaryLabels>
        </ErrorWrapper>

        <div className="other-title">{t('phaseFour.placeOfUseSubtitle')}</div>
        <p>{t('phaseFour.fillCarefully')}</p>

        <ErrorWrapper showError={!!formErrors['same-as']}>
          <SummaryLabels className={formErrors['same-as'] ? 'invalid' : ''}>
            <label className="radio-label">
              <div className="header">
                <input type="radio" id="same-as" checked={sameAsPostal === true} onChange={() => setSameAsPostal(true)} />
                <div className="description">{t('phaseFour.sameAsPostal')}</div>
              </div>
              <div className="description">
                <b>{getAddress(formValues, 'location')}</b>
              </div>
            </label>
            <label className="radio-label">
              <div className="header">
                <input type="radio" id="different" checked={sameAsPostal === false} onChange={() => setSameAsPostal(false)} />
                <div className="description">{t('phaseFour.notSameAsPostal')}</div>
              </div>
            </label>
          </SummaryLabels>
        </ErrorWrapper>

        {sameAsPostal !== undefined && formValues['metering-points'] && formValues['metering-points'].map((meteringPoint, index) => (
          <div key={index}>
            {(!(sameAsPostal === true && index === 0) || index > 0) && <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {!(sameAsPostal === true && index === 0) && <span>{t('phaseThree.placeOfUse')} {index + 1}</span>}
              {index > 0 && <button type="button" className="link-button" onClick={() => deleteMeteringPoint(index)}>{t('common.delete')} {t('phaseThree.placeOfUse')}</button>}
            </h4>}


            <Address
              zipCode={meteringPoint.address.zipCode}
              zipCodeError={formErrors[`metering-point-${index}-zipCode`]}
              cityName={meteringPoint.address.cityName}
              cityNameError={formErrors[`metering-point-${index}-cityName`]}
              streetName={meteringPoint.address.streetName}
              streetNameError={formErrors[`metering-point-${index}-streetName`]}
              houseNumber={meteringPoint.address.houseNumber}
              houseNumberError={formErrors[`metering-point-${index}-houseNumber`]}
              houseLetter={meteringPoint.address.houseLetter}
              houseLetterError={formErrors[`metering-point-${index}-houseLetter`]}
              residence={meteringPoint.address.residence}
              residenceError={formErrors[`metering-point-${index}-residence`]}
              hidecareOf={true}
              onlyAddressCheck={!((sameAsPostal && index > 0) || (!sameAsPostal))}
              status={meteringPoint.status}
              statusError={formErrors[`metering-point-${index}-status`]}
              onUpdate={(address, status) => updateMeteringPoint({ index, address, status })}
            />

            <Grid style={{ marginTop: '20px' }}>
              <div>
                {(meteringPoint.diffStart || index === 0) &&
                  <DateInput
                    label={t('phaseFour.contractStartDate')}
                    name={`metering-point-${index}-startDate`}
                    value={meteringPoint.startDate}
                    onChange={(e) => updateMeteringPoint({ index, startDate: e.target.value })}
                    min={minDate}
                    max={maxDate}
                    error={formErrors[`metering-point-${index}-startDate`]}
                  />}
              </div>
              {index > 0 && <div style={{ marginTop: '38px' }}>
                <label className="checkbox-label">
                  <input type="checkbox" name="diffStart" disabled={!meteringPoint.startDate} checked={meteringPoint.diffStart} onChange={(e) => updateMeteringPoint({ index, diffStart: e.target.checked })} />
                  {t('phaseFour.diffStartDate')}
                </label>
              </div>}
            </Grid>
            <HorizontalLine />
          </div>
        ))}

        {sameAsPostal != undefined && <div style={{ margin: '20px 0' }}>
          <button type="button" className="green" onClick={addMeteringPoint}>{t('common.add')} {t('phaseThree.placeOfUse')}</button>
        </div>}

        <ContractTypeText updateMaxDate={setMaxDate} updateMinDate={setMinDate} />

      </WhiteContainer>}

      {currentStep === 2 && <Actions>
        <button type="button" className={"primary" + (Object.keys(stepErrors).length > 0 ? ' disabled' : '')} onClick={() => toStep(3)}>{t('common.next')}</button>
      </Actions>}

      {currentStep !== 3 && <ErrorWrapper showError={showErrorStep === 3}>
        <ClosedWhiteContainer classes={showErrorStep === 3 ? 'error' : ''} text="Laskutustiedot" value={currentStep > 2 ? 'Muokkaa' : ''} handleClick={() => toStep(3)} />
      </ErrorWrapper>}
      {currentStep === 3 && <WhiteContainer id="step3">
        <h2>{t('phaseFour.invoicingInfo')}</h2>
        <div className="other-title">{t('phaseFour.invoiceMethod')}</div>

        <ErrorWrapper showError={!!formErrors['invoice-method']}>
          <SummaryLabels className={formErrors['invoice-method'] ? 'invalid' : ''}>
            {b2bInvoiceTypes.map((option) => (

              <label
                htmlFor={option.value}
                key={option.value}
                className="radio-label">

                <div className="header">
                  <input
                    type="radio"
                    id={option.value}
                    name="invoice-method"
                    value={option.value}
                    checked={option.value === formValues['invoice-method']}
                    onChange={handleFormChange}
                  />
                  <div className="description">{t(option.label)}</div>
                </div>
              </label>
            ))}
          </SummaryLabels>
        </ErrorWrapper>
        {formValues['invoice-method'] === 'email' && <Grid>
          <FormTextInput
            label={t('phaseFour.email')}
            type="email"
            fieldName="b2b-email"
            value={formValues['b2b-email']}
            error={formErrors['b2b-email']} />
        </Grid>}
        {formValues['invoice-method'] === 'b2b-einvoice' && (
          <div className="description" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <div>
              <label className="b2b-invoice">
              {t('phaseFour.b2bOperatorId')} 
                <p>{t('phaseFour.b2bOperatorDesc')}</p>
                <Grid>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <input type="text" name="b2b-operator" onChange={handleFormChange} value={formValues['b2b-operator']} className={formErrors['b2b-operator'] ? 'invalid' : undefined} />
                    {formErrors['b2b-operator'] && <span className="invalid">{formErrors['b2b-operator']}</span>}
                  </div>
                </Grid>
              </label>
            </div>
            <div>
              <label className="b2b-invoice">
              {t('phaseFour.einvoiceAddress')}
                <p>{t('phaseFour.einvoiceAddressDesc')}</p>
                <Grid>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <input type="text" name="b2b-address" onChange={handleFormChange} value={formValues['b2b-address']} className={formErrors['b2b-address'] ? 'invalid' : undefined} />
                    {formErrors['b2b-address'] && <span className="invalid">{formErrors['b2b-address']}</span>}
                  </div>
                </Grid>
              </label>
            </div>
          </div>
        )}

        <Grid style={{ marginTop: '20px' }}>
          <FormTextInput
            label={t('phaseFour.invoiceReference')}
            fieldName="invoice-reference"
            value={formValues['invoice-reference'] ?? ''}
            error={formErrors['invoice-reference']} />
        </Grid>

        <div style={{ marginTop: '20px' }}>
          <div className="other-title">{t('phaseFour.wherePaperInvoice')}</div>
          <p>{t('phaseFour.wherePaperInvoiceDesc')}</p>
          <SummaryLabels>
            <div>
              <label className="radio-label">
                <div className="header">
                  <input type="radio" name="separated-invoicing-address" value='off' checked={formValues['separated-invoicing-address'] != 'on'} onChange={handleSeparateInvoicingChange} />
                  <div className="description">
                    <div>{t('phaseFour.sameAsPostal')}</div>
                  </div>
                </div>
                <b>{getAddress(formValues, 'location')}</b>
              </label>
            </div>
            <div>
              <label className="radio-label">
                <div className="header">
                  <input type="radio" name="separated-invoicing-address" value='on' checked={formValues['separated-invoicing-address'] == 'on'} onChange={handleSeparateInvoicingChange} />
                  <div className="description">{t('phaseFour.notSameAsPostal')}</div>
                </div>
              </label>
            </div>
          </SummaryLabels>
          {formValues['separated-invoicing-address'] === 'on' && <AddressForm type="invoice" disableAddressCheck validationErrors={formErrors} />}
        </div>
      </WhiteContainer>}

      {currentStep === 3 && <>
        <ContentContainer>
          <p className={"center" + (Object.keys(formErrors).length > 0 ? ' error' : '')}>{t('phaseFour.fillToContinue')}</p>
        </ContentContainer>
        <Actions>
          <button type="button" className={"primary" + (Object.keys(stepErrors).length > 0 ? ' disabled' : '')} onClick={toNextPhase}>{t('common.next')}</button>
        </Actions>
      </>}

    </B2bPhaseFour>
  );
}

export default PhaseFour;
